import { getLowestPrice } from '~/modules/price/parsers';
import { useBrand } from '~/composables/useBrand';
import { fireLinkTag } from './tagManager';
import { DELIVERY_DAYS_BY_TIER } from '../../constants/common';

/**
 * @param {string} date
 * @returns {number}
 */
const getDiffInDays = (date) => {
  const arrivesBy = new Date(date).getTime();
  const today = Date.now();
  return Math.ceil((arrivesBy - today) / (1000 * 60 * 60 * 24));
};

/**
 * @param {string | undefined} arrivesByDate
 * @param {string} tier
 * @returns {string}
 */
const getStr1PoolDeliveryDays = (arrivesByDate, tier) => {
  const isTierDefaultOrNA = tier === 'Default' || tier === 'NA';
  const { isMcom } = useBrand();
  const isTier1or2 = tier === 'Tier1' || tier === 'Tier2';
  const isTier3 = tier === 'Tier3';
  if (isTier3 || isTierDefaultOrNA) {
    return isMcom ? DELIVERY_DAYS_BY_TIER.TIER3_OR_DEFAULT_1P_MCOM : DELIVERY_DAYS_BY_TIER.TIER3_OR_DEFAULT_1P_BCOM;
  }

  if (isTier1or2 && arrivesByDate) {
    return getDiffInDays(arrivesByDate).toString();
  }

  return 'NA';
};

/**
 * @param {string | undefined} arrivesByDate
 * @param {string} tier
 * @returns {string}
 */
const getVdfMkpDeliveryDays = (arrivesByDate, tier) => {
  const isTier3 = tier === 'Tier3';
  const isTier4 = tier === 'Tier4';
  const isTierDefault = tier === 'Default';
  const isTier1or2 = tier === 'Tier1' || tier === 'Tier2';

  if (arrivesByDate && (isTier1or2 || isTier3)) {
    return getDiffInDays(arrivesByDate).toString();
  }

  if (isTier4) {
    return DELIVERY_DAYS_BY_TIER.TIER4_VDF_MKP;
  }

  if (isTierDefault) {
    return DELIVERY_DAYS_BY_TIER.TIERNA_OR_DEFAULT;
  }

  return 'NA';
};

/**
 * @param {import('../../modules/availability/types').Ship} ship
 * @returns {string}
 * @param {boolean} abdVdfMkpTreatmentEnabled
 */
const getDeliveryDays = (ship, abdVdfMkpTreatmentEnabled) => {
  if (!ship) return 'NA';

  const { orderType, deliveryType } = ship;
  const tier = deliveryType?.tierLevel || 'NA';
  const arrivesByDate = deliveryType?.arrivesByDate;
  const isSTR1orPOOL = orderType === 'STR1' || orderType === 'POOL';
  const isSPECorORDR = orderType === 'SPEC' || orderType === 'ORDR';
  const isVdfMkp = orderType === 'DROP' || orderType === 'EXT';
  const isTierDefaultOrNA = tier === 'Default' || tier === 'NA';

  if (abdVdfMkpTreatmentEnabled && isVdfMkp) {
    return getVdfMkpDeliveryDays(arrivesByDate, tier);
  }

  if (isSTR1orPOOL) {
    return getStr1PoolDeliveryDays(arrivesByDate, tier);
  }

  if (!isSTR1orPOOL && !isSPECorORDR && isTierDefaultOrNA) {
    return DELIVERY_DAYS_BY_TIER.TIERNA_OR_DEFAULT;
  }

  return 'NA';
};

/**
 * @param {import('../../modules/availability/types').Pickup} pickup
 * @returns {string}
 * @param {boolean} abdVdfMkpTreatmentEnabled
 */
const getPickupDays = (pickup, abdVdfMkpTreatmentEnabled) => {
  if (pickup.bopsAvailability) {
    return `bops:${pickup.availabilityMessage}`;
  }

  const tier = pickup.deliveryType?.tierLevel || 'NA';
  const arrivesByDate = pickup.deliveryType?.arrivesByDate;
  const deliveryMessage = pickup.deliveryType?.deliveryMessage;
  const orderType = pickup.orderType;
  const isVdfMkp = orderType === 'DROP' || orderType === 'EXT';
  const isTier4 = tier === 'Tier4';

  const isTier1or2or3 = tier === 'Tier1' || tier === 'Tier2' || tier === 'Tier3';

  if (isTier1or2or3 && arrivesByDate) {
    const diffInDays = Math.ceil((new Date(arrivesByDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    return `Boss:${diffInDays.toString()}`;
  }

  if ((tier === 'Default' || tier === 'NA') && deliveryMessage) {
    const match = deliveryMessage.match(/(\d+)\s*[-–]\s*(\d+)/);
    if (match) {
      const [, start, end] = match;
      return `Boss:${start}:${end}`;
    }
  }

  if (isTier4 && abdVdfMkpTreatmentEnabled && isVdfMkp && deliveryMessage) {
    const rangeRegex = /\b(\d{1,3})\s*[-–]\s*(\d{1,3})\b/;
    const match = rangeRegex.exec(deliveryMessage);
    if (match) {
      const [, start, end] = match;
      return `Boss:${start}:${end}`;
    }
  }

  return 'NA';
};

/**
 * @param {import('../../modules/availability/types').ProductInfo} productInfo
 * @param {string} radioValue
 * @param {boolean} abdVdfMkpTreatmentEnabled
 * @returns {string}
 */
const productABDInfoTag = (productInfo, radioValue, abdVdfMkpTreatmentEnabled = false) => {
  const { ship, pickup } = productInfo;

  const isPickupSelected = radioValue === 'Pickup';
  const selectedOption = isPickupSelected ? pickup : ship;

  const tier = isPickupSelected && pickup?.bopsAvailability ? 'TierNA' : selectedOption?.deliveryType?.tierLevel;
  if (!tier) return null;

  let source = selectedOption.orderType || 'NA';
  if (source === 'ORDR') {
    source = productInfo.eligibleForPreOrder ? 'ORDR-PO' : 'ORDR-BO';
  }
  const deliveryDays = !isPickupSelected ? getDeliveryDays(ship, abdVdfMkpTreatmentEnabled) : 'NA';
  const pickupDays = isPickupSelected ? getPickupDays(pickup, abdVdfMkpTreatmentEnabled) : 'NA';

  const productABDInformation = `${source}#${deliveryDays}#${pickupDays}#${tier === 'NA' ? 'TierNA' : tier}`;
  return productABDInformation.toLowerCase();
};

/**
 * @typedef {object} params
 * @property {string} radioValue
 * @property {import('~/modules/price/parsers').TEMPSelectedPrice} selectedPrice
 * @property {import('../../modules/availability/types').ProductInfo} productInfo
 * @property {number} [quantity]
 * @property {boolean} [abdTreatmentEnabled]
 * @property {boolean} [abdVdfMkpTreatmentEnabled]
 * @property {boolean} [isChipFulfillmentEnabled]
 */

/**
 * @param {params} params
 * @returns {void}
 */
export default function tagAvailabilityRadioChange({
  productInfo,
  radioValue,
  selectedPrice,
  quantity = 1,
  abdTreatmentEnabled = false,
  abdVdfMkpTreatmentEnabled = false,
  isChipFulfillmentEnabled = false,
}) {
  const { bossAvailability = false, bopsAvailability = false, isBossUpc = false } = productInfo?.pickup || {};
  const isShippingSelected = radioValue === 'ShipIt';
  const isPickupSelected = radioValue === 'Pickup';
  const isSddSelected = radioValue === 'SameDay';

  let method = isShippingSelected ? 'ship' : 'pickup';

  if (isChipFulfillmentEnabled && isSddSelected) {
    if (productInfo?.shipFromStore?.deliveryMessage === 'Same-Day Delivery') {
      method = 'sdd';
    } else {
      method = 'ndd';
    }
  }

  if (isPickupSelected && isBossUpc) {
    method = 'boss';
  }

  const abdTag = productABDInfoTag(productInfo, radioValue, abdVdfMkpTreatmentEnabled);

  fireLinkTag({
    event_name: 'pdp change fulfilment method',
    link_name: 'pdp change fulfilment method',
    product_id: [productInfo.productId.toString()],
    product_name: [productInfo.productName],
    product_upc: [productInfo.selectedUpcId.toString()],

    product_price: [getLowestPrice(selectedPrice)?.toString() || ''],
    product_quantity: [quantity.toString()],

    product_fulfillment_method: [method],
    product_boss_select: [isBossUpc.toString()],
    product_bops_select: [isPickupSelected.toString()],
    product_boss_available: [bossAvailability?.toString()],
    product_bops_available: [bopsAvailability?.toString()],

    ...(abdTreatmentEnabled &&
      abdTag && {
        product_arrivalbydate_information: [abdTag],
      }),
  });
}

/**
 * @param {{
 * zipCodeStatus: boolean,
 * storeStatus: boolean,
 * productInfo: import('../../modules/availability/types').ProductInfo }} param
 */
export function fireShippingOptionChangeTag({ zipCodeStatus = false, storeStatus = false, productInfo, abdVdfMkpTreatmentEnabled = false }) {
  const isPickUpEligibleToFire = storeStatus && productInfo.selectedRadioButton === 'Pickup';
  const isShipItEligibleToFire = zipCodeStatus && productInfo.selectedRadioButton === 'ShipIt';

  const payload = {
    product_id: [productInfo.productId],
    product_arrivalbydate_information: [productABDInfoTag(productInfo, productInfo.selectedRadioButton, abdVdfMkpTreatmentEnabled)],
    event_name: `pdp ${zipCodeStatus ? 'zipcode' : 'store'} complete with updated abd info`,
  };

  if (isShipItEligibleToFire || isPickUpEligibleToFire) {
    fireLinkTag(payload);
  }
}
